import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { motion } from "framer-motion";
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Input } from "@/shared/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import {
  ArrowLeft, Pencil, Zap, Users, Building2, TrendingUp,
  CheckCircle2, XCircle, Clock, Activity, Search, ChevronLeft, ChevronRight, Percent, Receipt, Eye
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import * as adminService from "@/shared/api/services/adminService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";


const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30", label: "ACTIVE" },
  SUSPENDED: { icon: XCircle, color: "bg-red-500/20 text-red-400 border border-red-500/30", label: "SUSPENDED" },
  INACTIVE: { icon: Clock, color: "bg-amber-500/20 text-amber-400 border border-amber-500/30", label: "INACTIVE" },
};

const COLORS = ["#06b6d4", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

const DarkTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e3a5f", borderRadius: "10px", padding: "10px 14px" }}>
        <p style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "4px" }}>{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color, fontSize: "13px", fontWeight: 600 }}>
            {entry.name}: {entry.value?.toFixed(1)}K
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e3a5f", borderRadius: "10px", padding: "8px 12px" }}>
        <p style={{ color: "#06b6d4", fontSize: "13px", fontWeight: 600 }}>{payload[0].name}: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

export default function AdminEngineDetail() {
  const urlParams = new URLSearchParams(window.location.search);
  const engineId = urlParams.get("id");

  const [selectedTx, setSelectedTx] = useState(null);
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all", reseller: "all", merchant: "all" });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: engine } = useQuery({
    queryKey: ["engine-detail", engineId],
    queryFn: async () => adminService.getEngineById(engineId),
    enabled: !!engineId,
  });

  const { data: dashboardMetrics } = useQuery({
    queryKey: ["engine-dashboard-metrics", engineId],
    queryFn: async () => adminService.getEngineDashboardLiveMetrics(engineId),
    enabled: !!engineId,
  });


  const { data: allResellers = [] } = useQuery({
    queryKey: ["all-resellers"],
    queryFn: adminService.getAllResellers
  });

  const { data: allMerchants = [] } = useQuery({
    queryKey: ["all-merchants"],
    queryFn: adminService.getAllMerchants
  });

  const resellers = React.useMemo(() => {
    if (!engineId) return [];
    return allResellers.filter(r => r.createdByEngineId === engineId);
  }, [allResellers, engineId]);

  const merchants = React.useMemo(() => {
    if (!engineId) return [];
    const resellerIds = new Set(resellers.map(r => r.id));
    return allMerchants.filter(m => m.createdByEngineId === engineId || resellerIds.has(m.createdByResellerId));
  }, [allMerchants, resellers, engineId]);

  /* =========================================================================
     COMBINED TRANSACTIONS (Overview & All Transactions Tab)
  ========================================================================= */

  const filtersMapper = React.useMemo(() => ({
    status: txFilters.status !== "all" ? txFilters.status : undefined,
    type: txFilters.type !== "all" ? txFilters.type.toUpperCase() : undefined,
  }), [txFilters]);

  const merchantIdsForQuery = React.useMemo(() => {
    if (txFilters.merchant !== "all") return [txFilters.merchant];
    if (txFilters.reseller !== "all") {
      return merchants.filter(m => m.createdByResellerId === txFilters.reseller).map(m => m.id);
    }
    return merchants.map(m => m.id);
  }, [txFilters.merchant, txFilters.reseller, merchants]);

  const { data: combinedQRCodeAndWitData, isLoading: txLoading, error: combinedError } = useQuery({
    queryKey: ["combined-transactions", page, pageSize, txFilters.search, filtersMapper, merchantIdsForQuery],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      pageSize,
      txFilters.search || "",
      filtersMapper,
      merchantIdsForQuery.length > 0 ? merchantIdsForQuery : ["__NO_MERCHANTS__"]
    ),
    enabled: !!engineId && (allMerchants.length > 0 || allResellers.length > 0) // Enable once parent data is loaded
  });

  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];

  const transactions = React.useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: t.type?.toLowerCase() === 'sale' ? 'deposit' : (t.type?.toLowerCase() || "unknown"),
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      net_amount: t.net_amount,
      total_fee: t.total_fee,
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: t.type?.toLowerCase() === 'sale' || t.type?.toLowerCase() === 'deposit',
    })).sort((a, b) => new Date(b.created_date || b.createdAt).getTime() - new Date(a.created_date || a.createdAt).getTime());
  }, [combinedDataWithdrawalsAndQRCodes]);

  const status = statusConfig[engine?.status] || statusConfig.INACTIVE;
  const StatusIcon = status.icon;

  const statCards = [
    { title: "Today's Volume", value: `฿${Math.round(dashboardMetrics?.todayVolume) || 0}`, icon: TrendingUp },
    { title: "Total Volume", value: `฿${Math.round(dashboardMetrics?.totalVolume) || 0}`, icon: Activity },
    { title: "Success Rate", value: `${dashboardMetrics?.successRate.toFixed(2) || 0}%`, icon: Percent },
    { title: "Resellers", value: dashboardMetrics?.resellers || 0, icon: Users },
    { title: "Merchants", value: dashboardMetrics?.merchants || 0, icon: Building2 },
    { title: "Transactions", value: dashboardMetrics?.totalTransactions || 0, icon: Receipt },
  ];

  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const todayTxs = transactions.filter(t => {
    const txDate = new Date(t.created_date);
    return txDate >= todayStart && txDate <= todayEnd;
  });

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    const dayTxs = transactions.filter(t => new Date(t.created_date).toDateString() === date.toDateString() && t.status === "success");
    return {
      date: format(date, "MMM d"),
      deposits: dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
      withdrawals: dayTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
    };
  });

  const providerData = (engine?.allowed_providers || ["SCB", "KBANK", "BBL"]).map((code) => ({
    name: code, value: Math.floor(Math.random() * 100) + 20
  }));


  const paginatedTxs = transactions;
  const totalPages = combinedQRCodeAndWitData?.totalPages || 0;
  const filteredTxsLength = combinedQRCodeAndWitData?.total || 0;

  return (
    <div style={{ backgroundColor: "#060f1e", minHeight: "100vh", padding: "24px" }}>
      <style>{`
        .eng-dark-card { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 16px; overflow: hidden; margin-bottom: 0; }
        .eng-card-header { padding: 16px 20px; border-bottom: 1px solid #1e3a5f; }
        .eng-card-title { font-size: 15px; font-weight: 600; color: #e2e8f0; margin: 0; display: flex; align-items: center; gap: 8px; }
        .eng-card-content { padding: 20px; }
        .eng-input { background: #0a1628 !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important; border-radius: 10px !important; }
        .eng-input::placeholder { color: #475569 !important; }
        .eng-input:focus { border-color: #06b6d4 !important; box-shadow: 0 0 0 2px rgba(6,182,212,0.1) !important; }
        .eng-select-trigger { background: #0a1628 !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important; border-radius: 10px !important; }
        .eng-filter-bar { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 14px; padding: 16px; display: flex; flex-wrap: wrap; gap: 12px; }
        .eng-icon-btn { background: #0a1628; border: 1px solid #1e3a5f; border-radius: 8px; padding: 8px; color: #64748b; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; }
        .eng-icon-btn:hover { border-color: #06b6d4; color: #06b6d4; }
        .eng-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 500; font-family: monospace; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.3); color: #06b6d4; }
        .eng-pagination-btn { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 8px; padding: 6px 10px; color: #94a3b8; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; }
        .eng-pagination-btn:hover:not(:disabled) { border-color: #06b6d4; color: #06b6d4; }
        .eng-pagination-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .eng-table-head { color: #475569 !important; font-size: 12px !important; font-weight: 500 !important; border-bottom: 1px solid #1e3a5f !important; background: #0a1628; }
        .eng-table-row { border-bottom: 1px solid #1a2f4a !important; transition: all 0.15s; }
        .eng-table-row:hover { background: rgba(6,182,212,0.04) !important; }
        .eng-table-cell { color: #94a3b8 !important; font-size: 13px !important; }
        .space-y-6 > * + * { margin-top: 24px; }
        .space-y-4 > * + * { margin-top: 16px; }
      `}</style>

      <div className="space-y-6">
        {/* Header */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <Link to={createPageUrl("AdminEngines")}>
              <button
                className="eng-icon-btn"
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#06b6d4"; e.currentTarget.style.color = "#06b6d4"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e3a5f"; e.currentTarget.style.color = "#64748b"; }}
              >
                <ArrowLeft style={{ width: 20, height: 20 }} />
              </button>
            </Link>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 24px rgba(6,182,212,0.3)" }}>
                <Zap style={{ width: 32, height: 32, color: "#fff" }} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <h1 style={{ fontSize: 24, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>{engine?.name}</h1>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500 }} className={status.color}>
                    <StatusIcon style={{ width: 12, height: 12 }} />
                    {status.label}
                  </span>
                </div>
                <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: 14 }}>{engine?.code} • {engine?.user_email}</p>
              </div>
            </div>
          </div>
          <Link to={createPageUrl("AdminEngineForm") + `?id=${engineId}`}>
            <button
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                backgroundColor: "#06b6d4", color: "#fff",
                border: "none", borderRadius: 10, padding: "10px 18px",
                fontWeight: 600, fontSize: 14, cursor: "pointer",
                boxShadow: "0 0 16px rgba(6,182,212,0.4)", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#0891b2"; e.currentTarget.style.boxShadow = "0 0 24px rgba(6,182,212,0.6)"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#06b6d4"; e.currentTarget.style.boxShadow = "0 0 16px rgba(6,182,212,0.4)"; }}
            >
              <Pencil style={{ width: 16, height: 16 }} />
              Edit
            </button>
          </Link>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 16 }}>
          {statCards.map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              style={{ background: "#0d1f35", border: "1px solid #1e3a5f", borderRadius: 16, padding: 16, transition: "all 0.2s", cursor: "default" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#06b6d4"; e.currentTarget.style.boxShadow = "0 0 20px rgba(6,182,212,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e3a5f"; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <stat.icon style={{ width: 18, height: 18, color: "#06b6d4" }} />
                </div>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>{stat.title}</p>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList style={{ background: "#0d1f35", border: "1px solid #1e3a5f", borderRadius: 12, padding: 4 }}>
            {[
              { value: "analytics", icon: Activity, label: "Analytics" },
              { value: "transactions", icon: Receipt, label: "All Transactions" },
              { value: "resellers", icon: Users, label: "Resellers" },
              { value: "merchants", icon: Building2, label: "Merchants" },
            ].map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", fontSize: 13, fontWeight: 500, borderRadius: 8, color: "#64748b", transition: "all 0.2s" }}
                className="data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-400 data-[state=active]:border data-[state=active]:border-cyan-500/30 hover:text-cyan-400"
              >
                <tab.icon style={{ width: 15, height: 15 }} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ gridColumn: "span 2" }}>
                <div className="eng-dark-card">
                  <div className="eng-card-header"><p className="eng-card-title">Volume (7 Days)</p></div>
                  <div className="eng-card-content">
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="engDepositGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="engWithdrawGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                          <XAxis dataKey="date" stroke="#475569" fontSize={11} tick={{ fill: "#64748b" }} />
                          <YAxis stroke="#475569" fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={(v) => `${v}K`} />
                          <Tooltip content={<DarkTooltip />} />
                          <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#06b6d4" strokeWidth={2} fill="url(#engDepositGrad)" />
                          <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#engWithdrawGrad)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="eng-dark-card" style={{ height: "100%" }}>
                  <div className="eng-card-header"><p className="eng-card-title">Provider Distribution</p></div>
                  <div className="eng-card-content">
                    <div style={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={providerData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={5} dataKey="value">
                            {providerData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<PieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, justifyContent: "center" }}>
                      {providerData.map((e, i) => (
                        <div key={e.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: COLORS[i % COLORS.length] }} />
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>{e.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              <div style={{ gridColumn: "span 3" }}>
                <div className="eng-dark-card">
                  <div className="eng-card-header"><p className="eng-card-title">Recent 20 Transactions</p></div>
                  <div className="eng-card-content">
                    <AdminTransactionTable transactions={transactions.slice(0, 20)} isLoading={txLoading} onView={setSelectedTx} compact merchants={merchants} resellers={resellers} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* All Transactions Tab */}
          <TabsContent value="transactions">
            <div className="space-y-4">
              <div className="eng-filter-bar">
                <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                  <Search style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "#475569" }} />
                  <Input placeholder="Search by ID or reference..." value={txFilters.search} onChange={(e) => { setTxFilters({ ...txFilters, search: e.target.value }); setPage(0); }} className="eng-input" style={{ paddingLeft: 38 }} />
                </div>
                <Select value={txFilters.reseller} onValueChange={(v) => { setTxFilters({ ...txFilters, reseller: v }); setPage(0); }}>
                  <SelectTrigger className="eng-select-trigger" style={{ width: 160 }}><SelectValue placeholder="All Resellers" /></SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Resellers</SelectItem>
                    {resellers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={txFilters.merchant} onValueChange={(v) => { setTxFilters({ ...txFilters, merchant: v }); setPage(0); }}>
                  <SelectTrigger className="eng-select-trigger" style={{ width: 160 }}><SelectValue placeholder="All Merchants" /></SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Merchants</SelectItem>
                    {merchants.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={txFilters.type} onValueChange={(v) => { setTxFilters({ ...txFilters, type: v }); setPage(0); }}>
                  <SelectTrigger className="eng-select-trigger" style={{ width: 140 }}><SelectValue /></SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="deposit">Deposits</SelectItem>
                    <SelectItem value="withdrawal">Withdrawals</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={txFilters.status} onValueChange={(v) => { setTxFilters({ ...txFilters, status: v }); setPage(0); }}>
                  <SelectTrigger className="eng-select-trigger" style={{ width: 140 }}><SelectValue /></SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <AdminTransactionTable transactions={paginatedTxs} isLoading={txLoading} onView={setSelectedTx} merchants={merchants} resellers={resellers} />

              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 13, color: "#64748b" }}>Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, filteredTxsLength)} of {filteredTxsLength}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="eng-pagination-btn" onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft style={{ width: 16, height: 16 }} /></button>
                    <span style={{ fontSize: 13, color: "#94a3b8" }}>Page {page + 1} of {totalPages}</span>
                    <button className="eng-pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight style={{ width: 16, height: 16 }} /></button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Resellers Tab */}
          <TabsContent value="resellers">
            <div className="eng-dark-card">
              <div className="eng-card-header"><p className="eng-card-title">Resellers ({resellers.length})</p></div>
              <div className="eng-card-content">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderBottom: "1px solid #1e3a5f" }}>
                      {["Reseller", "Code", "Status", "Merchants", "Created", ""].map((h, i) => (
                        <TableHead key={i} style={{ color: "#475569", fontSize: 12, fontWeight: 500, background: "#0a1628" }}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resellers.map(r => (
                      <TableRow key={r.id} style={{ borderBottom: "1px solid #1a2f4a", cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(6,182,212,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <TableCell style={{ color: "#e2e8f0", fontSize: 13 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Users style={{ width: 16, height: 16, color: "#8b5cf6" }} />
                            </div>
                            <div>
                              <p style={{ fontWeight: 500, margin: 0, color: "#e2e8f0" }}>{r.name}</p>
                              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{r.user_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="eng-badge">{r.code}</span></TableCell>
                        <TableCell><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 500, display: "inline-block" }} className={statusConfig[r.status]?.color || ""}>{r.status}</span></TableCell>
                        <TableCell style={{ color: "#94a3b8", fontSize: 13 }}>{r.max_merchants || 10}</TableCell>
                        <TableCell style={{ color: "#64748b", fontSize: 12 }}>{r.created_date ? format(new Date(r.created_date), "MMM d, yyyy") : "N/A"}</TableCell>
                        <TableCell>
                          <Link to={createPageUrl("AdminResellerDetail") + `?id=${r.id}`}>
                            <button className="eng-icon-btn"><Eye style={{ width: 15, height: 15 }} /></button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Merchants Tab */}
          <TabsContent value="merchants">
            <div className="eng-dark-card">
              <div className="eng-card-header"><p className="eng-card-title">Merchants ({merchants.length})</p></div>
              <div className="eng-card-content">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderBottom: "1px solid #1e3a5f" }}>
                      {["Merchant", "Code", "Status", "Fee", "Created", ""].map((h, i) => (
                        <TableHead key={i} style={{ color: "#475569", fontSize: 12, fontWeight: 500, background: "#0a1628" }}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {merchants.map(m => (
                      <TableRow key={m.id} style={{ borderBottom: "1px solid #1a2f4a", cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(6,182,212,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <TableCell style={{ color: "#e2e8f0", fontSize: 13 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Building2 style={{ width: 16, height: 16, color: "#06b6d4" }} />
                            </div>
                            <div>
                              <p style={{ fontWeight: 500, margin: 0, color: "#e2e8f0" }}>{m.name}</p>
                              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{m.contact_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="eng-badge">{m.code}</span></TableCell>
                        <TableCell><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 500, display: "inline-block" }} className={statusConfig[m.status]?.color || ""}>{m.status}</span></TableCell>
                        <TableCell style={{ color: "#94a3b8", fontSize: 13 }}>{m.fee_value}{m.fee_type === "percentage" ? "%" : " THB"}</TableCell>
                        <TableCell style={{ color: "#64748b", fontSize: 12 }}>{m.created_date ? format(new Date(m.created_date), "MMM d, yyyy") : "N/A"}</TableCell>
                        <TableCell>
                          <Link to={createPageUrl("AdminMerchantDetail") + `?id=${m.id}`}>
                            <button className="eng-icon-btn"><Eye style={{ width: 15, height: 15 }} /></button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} engine={engine} />
    </div>
  );
}